import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Navigation } from "./navigation";
import { Contact } from "./contact";
import JsonData from "../data/data.json";

const BASE_URL = "https://cyberelectrix.co.nz";

const howToJsonLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Save On Your Electricity Bill During Winter",
  "description": "Use a plug-in timer switch to control your room heater more accurately than its built-in thermostat — and when to upgrade to a smart home ambient-temperature solution.",
  "image": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/img/Banner_16x_TR_Trim&ColorEdit.png`
  },
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "NZD",
    "value": "40"
  },
  "supply": [
    {
      "@type": "HowToSupply",
      "name": "Digital plug-in timer outlet ($20–$40, available at NZ hardware stores)"
    }
  ],
  "totalTime": "PT10M",
  "step": [
    {
      "@type": "HowToStep",
      "position": "1",
      "name": "Understand why your heater's built-in thermostat is inaccurate",
      "text": "Most portable heaters — oil column, panel, fan — have a thermostat that measures the temperature of the appliance body, not the ambient air in your room. It cuts the element when the casing reaches the set temperature, regardless of whether your room is actually warm.",
      "url": `${BASE_URL}/how-to`
    },
    {
      "@type": "HowToStep",
      "position": "2",
      "name": "Buy a digital timer outlet from a hardware store",
      "text": "A plug-in digital timer switch costs $20–$40 at most New Zealand hardware stores. Choose one with at least 15-minute programming increments and a daily or weekly schedule option.",
      "url": `${BASE_URL}/how-to`
    },
    {
      "@type": "HowToStep",
      "position": "3",
      "name": "Plug in your heater and set a consistent daily heating schedule",
      "text": "Plug the timer into the wall outlet, then plug your heater into the timer. Set a morning window (e.g. 6:30–8:30am) and an evening window (e.g. 5:00–10:00pm). The heater turns on before you need warmth and switches off automatically — no forgetting, no overnight waste.",
      "url": `${BASE_URL}/how-to`
    },
    {
      "@type": "HowToStep",
      "position": "4",
      "name": "Upgrade to ambient-temperature control for precision heating",
      "text": "If time-based switching isn't precise enough, a smart home heating solution uses a real ambient temperature sensor in your room — switching the heater on when the temperature drops below your threshold and off when it reaches your target. Contact CyberElectrix to scope a solution for your home.",
      "url": `${BASE_URL}/how-to`
    }
  ],
  "author": {
    "@type": "Organization",
    "name": "CyberElectrix",
    "url": BASE_URL
  },
  "publisher": {
    "@type": "Organization",
    "name": "CyberElectrix",
    "url": BASE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/img/Banner_16x_TR_Trim&ColorEdit.png`
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${BASE_URL}/how-to`
  }
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": BASE_URL
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "How to Save On Your Electricity Bill During Winter",
      "item": `${BASE_URL}/how-to`
    }
  ]
};

const WinterSavingsPage = () => {
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    setContactData(JsonData.Contact);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <Helmet>
        <title>How to Save On Your Electricity Bill During Winter | CyberElectrix Auckland</title>
        <meta
          name="description"
          content="Use a simple timer switch to control your room heater more accurately than its thermostat can. Auckland electricians explain how — and when to upgrade to smart home automation."
        />
        <meta
          name="keywords"
          content="save electricity bill winter Auckland, timer switch heater NZ, reduce heating costs Auckland, smart thermostat Auckland, winter electricity tips NZ"
        />
        <link rel="canonical" href={`${BASE_URL}/how-to`} />

        <meta property="og:title" content="How to Save On Your Electricity Bill During Winter | CyberElectrix" />
        <meta property="og:description" content="Use a simple timer switch to control your room heater more accurately than its thermostat can. Auckland electricians explain how — and when to upgrade." />
        <meta property="og:url" content={`${BASE_URL}/how-to`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="CyberElectrix" />
        <meta property="og:image" content={`${BASE_URL}/img/og-image.jpg`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How to Save On Your Electricity Bill During Winter | CyberElectrix" />
        <meta name="twitter:description" content="Use a simple timer switch to control your room heater more accurately than its thermostat can." />
        <meta name="twitter:image" content={`${BASE_URL}/img/og-image.jpg`} />

        <script type="application/ld+json">{JSON.stringify(howToJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Navigation />

      {/* Hero */}
      <div
        style={{
          background: "linear-gradient(to right, #181b2e 0%, #2a1a3e 50%, #181b2e 100%)",
          padding: "120px 0 60px",
          color: "#fff",
          textAlign: "center"
        }}
      >
        <div className="container">
          <i
            className="fa fa-thermometer-half"
            style={{ fontSize: "48px", color: "#f8825a", marginBottom: "20px", display: "block" }}
            aria-hidden="true"
          ></i>
          <h1 style={{ fontSize: "36px", fontWeight: "700", marginBottom: "16px" }}>
            How to Save On Your Electricity Bill During Winter
          </h1>
          <p style={{ fontSize: "18px", color: "#ccc", maxWidth: "720px", margin: "0 auto" }}>
            A practical, low-cost approach to heating your home more efficiently — and knowing when to go further.
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={{ background: "#f5f5f5", padding: "12px 0", borderBottom: "1px solid #e0e0e0" }}>
        <div className="container">
          <ol className="breadcrumb" style={{ background: "transparent", margin: 0, padding: 0, fontSize: "14px" }}>
            <li><Link to="/" style={{ color: "#f8825a" }}>Home</Link></li>
            <li className="active">Winter Electricity Savings</li>
          </ol>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "60px 0" }}>
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-md-offset-2">

              {/* Intro */}
              <p style={{ fontSize: "17px", lineHeight: "1.85", color: "#444", marginBottom: "40px" }}>
                Winter electricity bills in Auckland can spike sharply once heating season starts. Before you
                reach for a more expensive solution, there's a simple, inexpensive step that most households
                overlook: controlling your room heater with a timer switch rather than relying on its
                built-in thermostat.
              </p>

              {/* Section 1 */}
              <h2 style={{ fontSize: "26px", fontWeight: "700", color: "#181b2e", marginBottom: "16px", borderLeft: "4px solid #f8825a", paddingLeft: "16px" }}>
                The Problem With Your Heater's Thermostat
              </h2>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "16px" }}>
                Most portable room heaters — oil column heaters, panel heaters, fan heaters — have a thermostat
                dial. It seems logical: set it to a comfortable level and let the heater regulate itself.
              </p>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "16px" }}>
                The problem is that <strong>the thermostat on a room heater measures the temperature of the
                heater itself, not the ambient air in your room.</strong> It is a mechanical switch built into
                the appliance body, not a room sensor. When the heater's casing reaches the set temperature,
                the thermostat cuts the element — regardless of how cold or warm your room actually is.
              </p>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "40px" }}>
                The result: the heater may cycle off while your room is still cold, or conversely run
                longer than needed because a draught is cooling the appliance while the room is already warm.
                It's an inherently imprecise control mechanism for room comfort.
              </p>

              {/* Section 2 */}
              <h2 style={{ fontSize: "26px", fontWeight: "700", color: "#181b2e", marginBottom: "16px", borderLeft: "4px solid #f8825a", paddingLeft: "16px" }}>
                Timer Switching: A Simple, Effective Fix
              </h2>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "16px" }}>
                A digital timer outlet — a plug-in timer switch available from most hardware stores for
                around $20–$40 — lets you set specific ON and OFF times for your heater. The heater runs
                at full power on your schedule, independent of what its thermostat dial is doing.
              </p>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "16px" }}>
                A typical Auckland winter schedule might look like:
              </p>
              <ul style={{ fontSize: "16px", lineHeight: "2", color: "#555", paddingLeft: "24px", marginBottom: "16px" }}>
                <li><strong>6:30 am – 8:30 am:</strong> Morning warm-up before you leave for work</li>
                <li><strong>5:00 pm – 10:00 pm:</strong> Evening heating while you're home</li>
              </ul>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "40px" }}>
                The heater turns on before you need warmth and turns off automatically. You don't need to
                remember to switch it off before bed. The schedule is consistent every day — which is exactly
                what an inaccurate appliance-body thermostat cannot give you.
              </p>

              {/* Section 3 */}
              <h2 style={{ fontSize: "26px", fontWeight: "700", color: "#181b2e", marginBottom: "16px", borderLeft: "4px solid #f8825a", paddingLeft: "16px" }}>
                The Benefits
              </h2>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 40px 0" }}>
                {[
                  "Consistent heating schedule — warmth when you need it, off when you don't",
                  "No manual switching required — nothing to forget before bed",
                  "Reduces idle heating time that adds nothing to comfort",
                  "Works with any plug-in heater — no special appliance needed",
                  "Low upfront cost — a quality digital timer costs $20–$40"
                ].map((item, i) => (
                  <li key={i} style={{ padding: "10px 0 10px 32px", position: "relative", fontSize: "15px", color: "#444", borderBottom: i < 4 ? "1px solid #eee" : "none" }}>
                    <i className="fa fa-check" style={{ position: "absolute", left: 0, top: "12px", color: "#f8825a" }} aria-hidden="true"></i>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Section 4 */}
              <h2 style={{ fontSize: "26px", fontWeight: "700", color: "#181b2e", marginBottom: "16px", borderLeft: "4px solid #f8825a", paddingLeft: "16px" }}>
                The Limitation
              </h2>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "16px" }}>
                Timer switching is time-based, not temperature-based. It does not know how cold or warm
                your room is at any given moment. On an unusually mild winter day, the heater may still run
                through its full schedule. On an unexpectedly cold morning, it cannot start earlier on its own.
              </p>
              <p style={{ fontSize: "16px", lineHeight: "1.85", color: "#555", marginBottom: "40px" }}>
                For most Auckland households, this trade-off is entirely acceptable — the consistency gains
                far outweigh the occasional over- or under-run. But if you want genuine ambient-temperature
                control, you need a smarter solution.
              </p>

              {/* Section 5 — CTA */}
              <div
                style={{
                  background: "#f9f4ff",
                  border: "1px solid #e0d0f0",
                  borderRadius: "8px",
                  padding: "32px",
                  marginBottom: "40px"
                }}
              >
                <h2 style={{ fontSize: "22px", fontWeight: "700", color: "#181b2e", marginBottom: "12px", marginTop: 0 }}>
                  Want True Ambient Temperature Control?
                </h2>
                <p style={{ fontSize: "16px", lineHeight: "1.8", color: "#555", marginBottom: "20px" }}>
                  A smart home heating solution uses a real ambient temperature sensor in your room —
                  measuring the air around you, not the heater casing. It can switch your heater on when
                  the room drops below a set threshold, turn it off when it reaches your target, and
                  adapt to your routine automatically.
                </p>
                <p style={{ fontSize: "16px", lineHeight: "1.8", color: "#555", marginBottom: "24px" }}>
                  CyberElectrix designs and installs smart home automation systems — from single-room
                  temperature-sensing circuits to full building automation. If you're ready to move
                  beyond the timer, we can scope a solution that fits your home and budget.
                </p>
                <a
                  href="https://cyberelectrix.co.nz/#contact"
                  className="btn btn-custom btn-lg"
                  style={{ marginRight: "12px", marginBottom: "12px" }}
                >
                  Get a Free Consultation
                </a>
                <a
                  href="tel:+64275733478"
                  className="btn btn-lg"
                  style={{ background: "transparent", border: "2px solid #f8825a", color: "#f8825a", marginBottom: "12px" }}
                >
                  <i className="fa fa-phone" style={{ marginRight: "8px" }} aria-hidden="true"></i>
                  Call: 027 573 3478
                </a>
              </div>

              {/* Back link */}
              <p style={{ marginTop: "8px" }}>
                <Link to="/services/smart-home-automation-auckland" style={{ color: "#f8825a", fontSize: "14px", textDecoration: "none" }}>
                  <i className="fa fa-arrow-right" style={{ marginRight: "6px" }} aria-hidden="true"></i>
                  Learn more about Smart Home Automation in Auckland
                </Link>
              </p>

            </div>
          </div>
        </div>
      </div>

      <Contact data={contactData} />
    </div>
  );
};

export default WinterSavingsPage;
